import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Handshake, Plus, Loader2, KeyRound, PauseCircle, PlayCircle } from 'lucide-react';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', violet: '#6C4BB5', line: 'rgba(136,150,168,0.12)' };
const fmt$ = v => { if (!v) return '$0'; if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`; if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; };

function ChannelMix({ mix }) {
  if (!mix) return null;
  const rows = ['broker', 'affinity'].filter(c => mix.channels[c]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="channel-mix-stats">
      {rows.map(c => {
        const d = mix.channels[c];
        const color = c === 'affinity' ? C.violet : '#1A4C7C';
        return (
          <Card key={c} className="p-4 rounded-md bg-white" style={{ border: `1px solid ${C.line}`, borderLeft: `3px solid ${color}` }} data-testid={`channel-mix-${c}`}>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{c} channel</p>
            <div className="mt-2 grid grid-cols-4 gap-2 text-xs" style={{ color: C.slate }}>
              <div><p className="font-mono text-base font-bold" style={{ color: C.navy }}>{d.submissions || 0}</p>Submissions</div>
              <div><p className="font-mono text-base font-bold" style={{ color: C.navy }}>{d.bound || 0}</p>Bound</div>
              <div><p className="font-mono text-base font-bold" style={{ color: C.navy }}>{d.stp_rate ?? 0}%</p>STP rate</div>
              <div><p className="font-mono text-base font-bold" style={{ color: C.navy }}>{fmt$(d.gwp)}</p>Active GWP{c === 'affinity' && d.commission > 0 && <span className="block text-[10px]" style={{ color: C.violet }}>comm {fmt$(d.commission)}</span>}</div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function PortalUserForm({ partner, api, onDone }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/affinity/partners/${partner.partner_id}/portal-user`, { email, password, contact_name: contact });
      toast.success(data.message);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create portal login'); }
    setBusy(false);
  };
  return (
    <div className="mt-3 p-3 rounded-md space-y-2" style={{ background: 'rgba(108,75,181,0.05)' }}>
      <p className="text-xs font-medium" style={{ color: C.navy }}>Portal login for {partner.name} — same portal as brokers, submissions auto-labelled affinity.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" className="rounded-sm text-sm" data-testid={`portal-user-email-${partner.partner_id}`} />
        <Input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password (min 8 chars)" type="text" className="rounded-sm text-sm" data-testid={`portal-user-password-${partner.partner_id}`} />
        <Input value={contact} onChange={e => setContact(e.target.value)} placeholder="Contact name (optional)" className="rounded-sm text-sm" />
      </div>
      <Button size="sm" onClick={create} disabled={busy || !email || password.length < 8} className="rounded-md text-xs" style={{ background: C.violet }} data-testid={`portal-user-create-${partner.partner_id}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />} Create portal login
      </Button>
    </div>
  );
}

export default function AffinityPartnersPage() {
  const api = useApi();
  const [partners, setPartners] = useState(null);
  const [mix, setMix] = useState(null);
  const [lines, setLines] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [portalFor, setPortalFor] = useState(null);
  const [form, setForm] = useState({ name: '', agreement_ref: '', commission_pct: 10, lines_allowed: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/affinity/partners').then(r => setPartners(r.data.partners)).catch(() => setPartners([]));
    api.get('/affinity/analytics').then(r => setMix(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => {
    load();
    api.get('/knowledge/layers').then(r => setLines(r.data.lines || [])).catch(() => {});
  }, [load, api]);

  const createPartner = async () => {
    setBusy(true);
    try {
      await api.post('/affinity/partners', form);
      toast.success(`Affinity partner ${form.name} registered`);
      setShowCreate(false);
      setForm({ name: '', agreement_ref: '', commission_pct: 10, lines_allowed: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create partner'); }
    setBusy(false);
  };

  const toggleStatus = async (p) => {
    const status = p.status === 'active' ? 'paused' : 'active';
    try {
      await api.put(`/affinity/partners/${p.partner_id}`, { status });
      toast.success(`${p.name} ${status === 'active' ? 'reactivated' : 'paused'} — ${status === 'paused' ? 'intake blocked under this agreement' : 'intake open'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const toggleLine = (l) => setForm(f => ({ ...f, lines_allowed: f.lines_allowed.includes(l) ? f.lines_allowed.filter(x => x !== l) : [...f.lines_allowed, l] }));

  if (partners === null) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;

  return (
    <div className="space-y-4" data-testid="affinity-partners-page">
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs max-w-2xl" style={{ color: C.slate }}>
          <Handshake size={13} className="inline mr-1" style={{ color: C.violet }} />
          Partner-sourced business flows through its own labelled lane — submissions carry the partner snapshot at intake,
          and the agreement commission % is stamped <b>deterministically at bind</b> (Pen-side, from the record here).
        </p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)} className="rounded-md text-xs shrink-0" style={{ background: C.violet }} data-testid="new-partner-btn">
          <Plus className="w-3 h-3 mr-1" /> Register partner
        </Button>
      </div>

      <ChannelMix mix={mix} />

      {showCreate && (
        <Card className="p-4 rounded-md bg-white space-y-3" style={{ border: `1px solid ${C.line}`, borderLeft: `3px solid ${C.violet}` }} data-testid="create-partner-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-xs" style={{ color: C.slate }}>Partner name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. National Fitness Association" className="mt-1 rounded-sm text-sm" data-testid="partner-name-input" /></div>
            <div><Label className="text-xs" style={{ color: C.slate }}>Agreement ref</Label>
              <Input value={form.agreement_ref} onChange={e => setForm(f => ({ ...f, agreement_ref: e.target.value }))} placeholder="e.g. AFF-2026-001" className="mt-1 rounded-sm text-sm" data-testid="partner-agreement-input" /></div>
            <div><Label className="text-xs" style={{ color: C.slate }}>Commission %</Label>
              <Input type="number" min={0} max={50} step={0.5} value={form.commission_pct} onChange={e => setForm(f => ({ ...f, commission_pct: parseFloat(e.target.value) || 0 }))} className="mt-1 rounded-sm text-sm" data-testid="partner-commission-input" /></div>
          </div>
          <div>
            <Label className="text-xs" style={{ color: C.slate }}>Lines allowed under the agreement (none selected = all lines)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {lines.map(l => (
                <button key={l} type="button" onClick={() => toggleLine(l)} data-testid={`partner-line-${l}`}
                  className="text-[11px] px-2 py-1 rounded-sm border transition-colors"
                  style={form.lines_allowed.includes(l)
                    ? { background: C.violet, color: '#fff', borderColor: C.violet }
                    : { background: '#fff', color: C.slate, borderColor: 'rgba(136,150,168,0.3)' }}>
                  {l.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={createPartner} disabled={busy || form.name.trim().length < 2} className="rounded-md text-xs" style={{ background: C.violet }} data-testid="partner-create-submit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />} Create partner
          </Button>
        </Card>
      )}

      {partners.length === 0 ? (
        <Card className="p-8 rounded-md text-center" style={{ border: `1px solid ${C.line}` }}>
          <Handshake className="w-8 h-8 mx-auto mb-3" style={{ color: C.slate }} />
          <p className="text-sm font-medium" style={{ color: C.navy }}>No affinity partners yet</p>
          <p className="text-xs mt-1" style={{ color: C.slate }}>Register a partner to open the affinity lane — staff can then tag intake, or give the partner a portal login.</p>
        </Card>
      ) : partners.map(p => (
        <Card key={p.partner_id} className="p-4 rounded-md bg-white" style={{ border: `1px solid ${C.line}`, borderLeft: `3px solid ${p.status === 'active' ? C.violet : C.slate}` }} data-testid={`partner-row-${p.partner_id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm" style={{ color: C.navy }}>{p.name}</h3>
                <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={p.status === 'active' ? { background: 'rgba(29,158,117,0.1)', color: C.green } : { background: '#EEF1F5', color: C.slate }}>{p.status}</span>
                {p.portal_user_email && <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-medium" style={{ background: 'rgba(108,75,181,0.08)', color: C.violet }} data-testid={`portal-user-chip-${p.partner_id}`}><KeyRound size={9} className="inline mr-0.5" />{p.portal_user_email}</span>}
              </div>
              <p className="text-xs mt-1" style={{ color: C.slate }}>
                Agreement: <span className="font-mono">{p.agreement_ref || '—'}</span> · Commission: <b style={{ color: C.navy }}>{p.commission_pct}%</b> · Lines: {(p.lines_allowed || []).length ? p.lines_allowed.map(l => l.replace(/_/g, ' ')).join(', ') : 'all'}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              {!p.portal_user_email && (
                <Button size="sm" variant="outline" onClick={() => setPortalFor(portalFor === p.partner_id ? null : p.partner_id)} className="rounded-md text-xs" style={{ borderColor: C.violet, color: C.violet }} data-testid={`portal-user-btn-${p.partner_id}`}>
                  <KeyRound className="w-3 h-3 mr-1" /> Portal login
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => toggleStatus(p)} className="rounded-md text-xs" data-testid={`toggle-partner-${p.partner_id}`}
                style={p.status === 'active' ? { borderColor: C.red, color: C.red } : { borderColor: C.green, color: C.green }}>
                {p.status === 'active' ? <><PauseCircle className="w-3 h-3 mr-1" /> Pause</> : <><PlayCircle className="w-3 h-3 mr-1" /> Reactivate</>}
              </Button>
            </div>
          </div>
          {portalFor === p.partner_id && !p.portal_user_email && (
            <PortalUserForm partner={p} api={api} onDone={() => { setPortalFor(null); load(); }} />
          )}
        </Card>
      ))}
    </div>
  );
}
